import { ChangeDetectorRef, Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import * as Sentry from '@sentry/angular';
import { BaseChartDirective } from 'ng2-charts';
import { GaugeComponent } from '../../../shared/components/gauge/gauge.component';
import { ChartConfiguration, ChartData } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { faPersonChalkboard, faPeopleGroup, faPrint } from '@fortawesome/free-solid-svg-icons';

import { ManagementUser } from '../../../core/models/management-user.model';
import { LoadingService } from '../../../core/services/loading.service';
import { ReportsService } from '../../../core/services/reports.service';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { schoolDashboardIntroSteps } from './school-dashboard.intro-steps';

interface TeacherStat {
  name: string;
  displayValue: string;
  value: number;
}

@Component({
  selector: 'wf-school-dashboard',
  templateUrl: './school-dashboard.component.html',
  styleUrls: ['./school-dashboard.component.css'],
  standalone: false,
})
export class SchoolDashboardComponent implements OnInit {
  currentUser: ManagementUser | null = null;
  loadError: string = '';

  teacherIcon = faPersonChalkboard;
  studentsIcon = faPeopleGroup;
  printIcon = faPrint;

  // Top summary cards
  teacherCount: number = 0;
  enrolledCount: number = 0;

  // Date ranges
  dateRanges: any[] = [];
  dateRange: any = { startDisplay: '-', endDisplay: '-' };

  // Classroom Engagement
  classroomsAbove60: number = 0;
  totalClassrooms: number = 0;
  classroomEngagementPercent: number = 0;
  topClassroomEngagement: TeacherStat[] = [];

  // Teacher Usage
  teachersWithLoginCount: number = 0;
  teacherLoginPercent: number = 0;
  topTeacherLogins: TeacherStat[] = [];

  // Student Engagement - Donut
  donutType: 'doughnut' = 'doughnut';
  donutData: ChartData<'doughnut'> = {
    labels: ['80+ min', '60-79 min', '40-59 min', '20-39 min', '0-19 min'],
    datasets: [{
      data: [0, 0, 0, 0, 0],
      backgroundColor: ['#B2D235', '#0095DA', '#007236', '#8CCEEE', '#555555'],
      borderWidth: 0,
    }],
  };
  donutOptions: ChartConfiguration<'doughnut'>['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '60%',
    layout: {
      padding: 30,
    },
    font: {
      family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: (context: any) => (context.dataset.data[context.dataIndex] as number) > 0,
        color: '#555555',
        font: {
          size: 12,
          family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
        },
        formatter: (value: number, context: any) => {
          const total = (context.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          return total > 0 ? `${Math.round((value / total) * 100)}%` : '';
        },
        anchor: 'end',
        align: 'end',
      },
    },
  };
  donutPlugins = [ChartDataLabels];

  // Breakdown table [index 0 = 0-19 min, index 4 = 80+ min]
  breakdownBins: number[] = [0, 0, 0, 0, 0];
  breakdownPercents: string[] = ['0%', '0%', '0%', '0%', '0%'];
  total60PlusPercent: string = '0';

  topStudentUsageByTeacher: TeacherStat[] = [];

  // Student Units (from progress data)
  studentsWithUnit: number = 0;
  totalStudentsUnit: number = 0;
  unitBarPercent: number = 0;

  readonly tourSteps = schoolDashboardIntroSteps;

  @ViewChildren(BaseChartDirective) charts!: QueryList<BaseChartDirective>;
  @ViewChild('classroomGauge') classroomGauge!: GaugeComponent;
  @ViewChild('teacherGauge') teacherGauge!: GaugeComponent;

  constructor(
    private route: ActivatedRoute,
    private reportsService: ReportsService,
    private sessionStorageService: SessionStorageService,
    private loadingService: LoadingService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.sessionStorageService.userData();

    const resolveData = this.route.snapshot.data['resolveData'];
    if (!resolveData || typeof resolveData !== 'object' || !resolveData.usageData) {
      this.loadError = 'There was an error loading the dashboard. Please refresh the page or try again later.';
      return;
    }
    const usageData = resolveData.usageData;
    const progressData = resolveData.progressData;

    this.dateRanges = usageData.ranges || [];
    if (this.dateRanges.length) {
      this.dateRange = this.dateRanges[0];
    }

    const schoolId = this.currentUser!.schoolID;

    if (usageData.schools?.[schoolId]) {
      this.enrolledCount = usageData.schools[schoolId].enrolled || 0;
    }

    const initialSchoolData = usageData.rangeData?.schools?.[schoolId];

    this.teacherCount = initialSchoolData?.teachers
      ? Object.keys(initialSchoolData.teachers).length
      : 0;

    this.processRangeData(initialSchoolData);
    this.processProgressData(progressData?.schools?.[schoolId]);
  }

  processRangeData(schoolData: any): void {
    this.classroomsAbove60 = 0;
    this.totalClassrooms = 0;
    this.teachersWithLoginCount = 0;
    this.classroomEngagementPercent = 0;
    this.teacherLoginPercent = 0;
    this.breakdownBins = [0, 0, 0, 0, 0];
    this.breakdownPercents = ['0%', '0%', '0%', '0%', '0%'];
    this.total60PlusPercent = '0';
    this.topClassroomEngagement = [];
    this.topTeacherLogins = [];
    this.topStudentUsageByTeacher = [];
    this.donutData.datasets[0].data = [0, 0, 0, 0, 0];

    if (!schoolData?.teachers) {
      if (this.charts) {
        this.charts.forEach(c => c.update());
      }
      return;
    }

    const teachers = Object.values(schoolData.teachers) as any[];
    this.totalClassrooms = teachers.length;

    let totalStudents = 0;
    const bins = [0, 0, 0, 0, 0]; // [0-19, 20-39, 40-59, 60-79, 80+]
    // These two per-teacher rankings are intentionally different metrics:
    // "Classroom engagement" ranks by the share of students meeting the 60-min/week
    // goal; "student usage" ranks by the share of students who are active at all
    // (logged in), which is why usage% >= engagement% per teacher.
    const classroomStats: TeacherStat[] = [];
    const loginStats: TeacherStat[] = [];
    const usageStats: TeacherStat[] = [];

    teachers.forEach((teacher: any) => {
      const students = Object.values(teacher.students || {}) as any[];
      const studentCount = students.length;
      const teacherName = `${teacher.firstName} ${teacher.lastName}`;

      if (teacher.numberOfLogins > 0) {
        this.teachersWithLoginCount++;
      }

      loginStats.push({
        name: teacherName,
        value: teacher.numberOfLogins,
        displayValue: `${teacher.numberOfLogins}`,
      });

      let classroomTotalUsage = 0;
      let classroomStudentsAbove60 = 0;
      let classroomStudentsActive = 0;

      students.forEach((student: any) => {
        const usage = student.usage || 0;
        totalStudents++;
        classroomTotalUsage += usage;

        if (usage < 20) bins[0]++;
        else if (usage < 40) bins[1]++;
        else if (usage < 60) bins[2]++;
        else if (usage < 80) bins[3]++;
        else bins[4]++;

        if (usage >= 60) classroomStudentsAbove60++;
        if ((student.sessionCount || 0) > 0) classroomStudentsActive++;
      });

      // The "X of Y classrooms" count (gauge) is based on each classroom's AVERAGE
      // usage clearing 60 min, whereas the "Top Classroom Engagement" list below ranks
      // by the SHARE of a classroom's students clearing 60 min. These are deliberately
      // different metrics, so a teacher can top the list without being in the count.
      const avgClassroomUsage = studentCount > 0 ? classroomTotalUsage / studentCount : 0;
      if (avgClassroomUsage >= 60) {
        this.classroomsAbove60++;
      }

      const engagementPct = studentCount > 0 ? Math.round((classroomStudentsAbove60 / studentCount) * 100) : 0;
      classroomStats.push({
        name: teacherName,
        value: engagementPct,
        displayValue: `${engagementPct}%`,
      });

      const usagePct = studentCount > 0 ? Math.round((classroomStudentsActive / studentCount) * 100) : 0;
      usageStats.push({
        name: teacherName,
        value: usagePct,
        displayValue: `${usagePct}%`,
      });
    });

    this.classroomEngagementPercent = this.totalClassrooms > 0
      ? Math.round((this.classroomsAbove60 / this.totalClassrooms) * 100) : 0;

    this.teacherLoginPercent = this.totalClassrooms > 0
      ? Math.round((this.teachersWithLoginCount / this.totalClassrooms) * 100) : 0;

    this.topClassroomEngagement = [...classroomStats]
      .sort((a, b) => b.value - a.value)
      .slice(0, 3);

    this.topTeacherLogins = [...loginStats]
      .sort((a, b) => b.value - a.value)
      .slice(0, 3);

    this.topStudentUsageByTeacher = [...usageStats]
      .sort((a, b) => b.value - a.value)
      .slice(0, 3);

    this.breakdownBins = bins;
    this.breakdownPercents = bins.map(b =>
      totalStudents > 0 ? `${Math.round((b / totalStudents) * 100)}%` : '0%'
    );

    const plus60Total = bins[3] + bins[4];
    const plus60Pct = totalStudents > 0 ? Math.round((plus60Total / totalStudents) * 100) : 0;
    this.total60PlusPercent = `${plus60Pct}`;

    // Donut data order: [80+, 60-79, 40-59, 20-39, 0-19]
    this.donutData.datasets[0].data = [bins[4], bins[3], bins[2], bins[1], bins[0]];

    if (this.charts) {
      this.charts.forEach(c => c.update());
    }
  }

  processProgressData(schoolData: any): void {
    if (!schoolData) return;
    this.studentsWithUnit = schoolData.totalStudentsWithUnitPerWeek || 0;
    this.totalStudentsUnit = schoolData.totalStudents || 0;
    this.unitBarPercent = this.totalStudentsUnit > 0
      ? Math.round((this.studentsWithUnit / this.totalStudentsUnit) * 100) : 0;
  }

  printPage(): void {
    window.print();
  }

  loadRangeData(): void {
    this.loadError = '';
    this.loadingService.start();
    const schoolId = this.currentUser!.schoolID;
    const districtId = this.reportsService.getSelectedDistrictForReports().districtID;

    this.reportsService.getDistrictUsageRangeData(districtId, this.dateRange.start, this.dateRange.end).subscribe({
      next: (rangeData: any) => {
        this.processRangeData(rangeData.schools?.[schoolId]);
        this.changeDetector.detectChanges();

        // Gauges only compute their arc on init; the percentages just changed,
        // so trigger a recalc after change detection has updated their inputs.
        this.classroomGauge?.calculateValues();
        this.teacherGauge?.calculateValues();
        this.loadingService.finish();
      },
      error: (err: any) => {
        this.loadingService.finish();
        this.loadError = 'There was an error loading data for the selected date range. Please try again.';
        Sentry.captureException(err, {
          tags: { section: 'reports', report: 'school-dashboard' },
        });
      },
    });
  }
}
